import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "../ui/sonner";
import { chunkUpload } from "../EnginePanel";
import { evidenceUrl } from "../EvidenceLightbox";
import { CheckCircle2, Circle, Loader2, Trash2, Upload } from "lucide-react";
import { fmtDT } from "../../lib/date";

/**
 * WF-04-01-07 厨房橱柜三步提交面板。
 *
 * 【需求】2026-10-29 老板指出框架结构阶段漏了「PM 拍摄厨房 → 发橱柜厂出图 → 业主签字」这条链，
 * 要求做成三个"把文件交给系统"的步骤。分工：现场 PM 传厨房照片、线上 PM 传设计图、现场 PM 传业主签字图。
 *
 * 【为什么不做成在线审核流】现有 engine_phase10 的 Cabinet 布局审核是「供应商链接审核 + 业主在线手写签名」，
 * 面向双方都愿意点链接的场景。这里要的是三段留档，硬套审核流会让 PM 多填供应商姓名电话、多生成短信链接。
 *
 * 【门禁提示】三步齐全后端才允许完成任务卡。前端把"还缺哪几步"直接摆在面板上，
 * 避免 PM 点完成被 400 拦回却不知道缺什么。
 */
const STEP_STYLE = {
  photos: { ring: "border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-900", chip: "bg-sky-100 text-sky-800 border-sky-200" },
  designDrawings: { ring: "border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-900", chip: "bg-violet-100 text-violet-800 border-violet-200" },
  signedDrawings: { ring: "border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900", chip: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

export const CabinetFlowPanel = ({ api, pid, zh, canWrite, onChanged }) => {
  const T = (a, b) => (zh ? a : b);
  const [d, setD] = useState(null);
  const [busyStep, setBusyStep] = useState("");
  const [pct, setPct] = useState(0);
  const [err, setErr] = useState("");
  const inputs = { photos: useRef(null), designDrawings: useRef(null), signedDrawings: useRef(null) };

  const load = useCallback(
    () => api.get(`/projects/${pid}/engine/cabinet-flow`).then(({ data }) => setD(data)).catch(() => {}),
    [api, pid]
  );
  useEffect(() => { load(); }, [load]);

  /**
   * 多文件上传：一次可选多张，逐张上传后再一次性提交给后端追加。
   * 为什么逐张 chunkUpload 再批量 POST：chunkUpload 是分片上传（大图纸 PDF 可能几十 MB），
   * 而落库接口只需要最终 fileId 列表，这样既不受单请求体积限制，也能显示逐张进度。
   */
  const upload = async (step, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusyStep(step); setPct(0); setErr("");
    try {
      const uploaded = [];
      for (let i = 0; i < files.length; i += 1) {
        const up = await chunkUpload(api, files[i], (p) => setPct(Math.round(((i + p / 100) / files.length) * 100)));
        uploaded.push(up);
      }
      const { data } = await api.post(`/projects/${pid}/engine/cabinet-flow/${step}/files`, { files: uploaded });
      setD(data.state);
      toast.success(T(`已上传 ${data.added} 份`, `${data.added} file(s) uploaded`));
      onChanged && onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || T("上传失败", "Upload failed");
      setErr(msg); toast.error(msg);
    } finally { setBusyStep(""); setPct(0); }
  };

  const del = async (step, file) => {
    if (!window.confirm(T(`删除「${file.filename}」？`, `Remove "${file.filename}"?`))) return;
    try {
      const { data } = await api.delete(`/projects/${pid}/engine/cabinet-flow/${step}/files/${file.fileId}`);
      setD(data.state);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || T("删除失败", "Delete failed")); }
  };

  if (!d) return null;
  const steps = d.steps || [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-2" data-testid="trace-ws-cabinet-flow">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          {T("橱柜三步资料 · 三步齐全才能完成本任务", "Cabinet three-step submissions · all three required")}
        </p>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${d.allDone ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}
          data-testid="trace-ws-cabinet-progress" data-done={d.doneCount} data-total={d.totalSteps}>
          {d.doneCount}/{d.totalSteps}
        </span>
      </div>

      {err && <p className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="trace-ws-cabinet-err">{err}</p>}

      <div className="space-y-2">
        {steps.map((s) => {
          const st = STEP_STYLE[s.key] || STEP_STYLE.photos;
          const isBusy = busyStep === s.key;
          return (
            <div key={s.key} className="rounded-lg border border-slate-100 p-2" data-testid={`trace-ws-cabinet-step-${s.key}`} data-done={s.done}>
              <div className="flex items-start gap-1.5">
                {s.done
                  ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" data-testid={`trace-ws-cabinet-done-${s.key}`} />
                  : <Circle className="h-3.5 w-3.5 text-slate-300 shrink-0 mt-0.5" data-testid={`trace-ws-cabinet-todo-${s.key}`} />}
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5 flex-wrap">
                    {s.order}. {zh ? s.zh : s.en}
                    <span className={`text-[9px] px-1 py-px rounded border font-semibold ${st.chip}`}>
                      {s.ownerRole === "cdc" ? T("线上 PM", "Online PM") : T("现场 PM", "Field PM")}
                    </span>
                    {s.count > 0 && <span className="text-[9px] text-slate-400">{T(`${s.count} 份`, `${s.count} file(s)`)}</span>}
                  </p>
                  <p className="text-[10px] text-slate-400 leading-relaxed mt-0.5">{zh ? s.hintZh : s.hintEn}</p>
                </div>
                <button type="button" disabled={!canWrite || isBusy} onClick={() => inputs[s.key].current?.click()}
                  data-testid={`trace-ws-cabinet-upload-${s.key}`}
                  className={`shrink-0 rounded-md border px-2 py-1 text-[10px] font-bold inline-flex items-center gap-1 transition-colors disabled:opacity-40 ${st.ring}`}>
                  {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  {isBusy ? `${pct}%` : T("上传", "Upload")}
                </button>
                <input ref={inputs[s.key]} type="file" multiple className="hidden"
                  accept="image/*,application/pdf,.dwg,.dxf,.doc,.docx,.xls,.xlsx"
                  data-testid={`trace-ws-cabinet-input-${s.key}`}
                  onChange={(e) => { upload(s.key, e.target.files); e.target.value = ""; }} />
              </div>
              {(s.files || []).length > 0 && (
                <ul className="mt-1.5 space-y-1" data-testid={`trace-ws-cabinet-files-${s.key}`}>
                  {s.files.map((f) => (
                    <li key={f.fileId} className="flex items-center gap-1.5 text-[10.5px] text-slate-700 rounded bg-slate-50 border border-slate-100 px-1.5 py-1">
                      <a href={evidenceUrl(f.fileId)} target="_blank" rel="noreferrer" className="truncate flex-1 hover:underline" title={f.filename}>{f.filename}</a>
                      <span className="text-[9px] text-slate-400 shrink-0 hidden sm:inline">{f.by} · {fmtDT(f.at)}</span>
                      {canWrite && (
                        <button type="button" onClick={() => del(s.key, f)} className="text-slate-300 hover:text-red-600 shrink-0"
                          data-testid={`trace-ws-cabinet-del-${s.key}-${f.fileId}`} title={T("删除", "Remove")}><Trash2 className="h-3 w-3" /></button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {!d.allDone && (
        <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="trace-ws-cabinet-missing">
          {T("还缺：", "Missing: ")}{(d.missing || []).join("、")}
        </p>
      )}
      {d.allDone && (
        <p className="text-[10px] text-emerald-700 font-semibold inline-flex items-center gap-1" data-testid="trace-ws-cabinet-ready">
          <CheckCircle2 className="h-3 w-3" />{T("三步资料齐全，可以完成任务", "All three steps filed — you may complete this task")}
        </p>
      )}
    </div>
  );
};
